import axios, { AxiosRequestConfig } from "axios";
import Swal from "sweetalert2";
import { flujoEnum, FlujoStatus } from "../types/enumTypes";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";

export const obtenerDatos = async (
    anio: number,
    mes: number,
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatales",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void
) => {
    setLoading(true);
    setError(null);
    const requestData = { anio, mes, tipo, origen };
    try {
        const response = await axios.post(process.env.REACT_APP_APPLICATION_DISTRIBUCIONES + "/GenerarReporteFondos", requestData,
            {
                headers: {
                    "Content-Type": "application/json", // Tipo de contenido
                    "x-api-key": process.env.REACT_APP_API_KEY || "", // Otro encabezado personalizado (si es necesario)
                },
            }
        );
        const formatTwoDecimals = (num: number): string => {
            return num.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        };
        const toNumber = (value: string | undefined): number => {
            if (!value) return 0;
            return parseFloat(value.replace(/,/g, "")) || 0;
        };
        // Obtener las columnas numéricas dinámicamente
        const obtenerColumnasNumericas = (data: Record<string, string>[]): string[] => {
            if (data.length === 0) return [];
            const excludeFields = ['ClaveEstado', 'OrdenSFTGNL']; // Excluir campos no numéricos
            return Object.keys(data[0]).filter((key) => {
                if (excludeFields.includes(key)) return false;
                const value = data[0][key];
                return typeof value === "string" && !isNaN(parseFloat(value.replace(/,/g, "")));
            });
        };
        const columnasNumericas = obtenerColumnasNumericas(response.data);
        // Agregar la suma de cada fila manteniendo decimales
        const datosConTotales = response.data.map((fila: Record<string, string>) => {
            const totalFila = columnasNumericas.reduce((suma: number, columna: string) => {
                return suma + toNumber(fila[columna]);
            }, 0);
            return {
                ...fila,
                TOTALES: formatTwoDecimals(totalFila)
            };
        });
        // Crear una fila final con los totales de cada columna
        const filaTotales: Record<string, string | number> = { Municipio: "Totales" };
        columnasNumericas.forEach((columna) => {
            const sumaColumna = datosConTotales.reduce((suma: number, fila: Record<string, string | number>) => {
                return suma + toNumber(fila[columna] as string);
            }, 0);
            filaTotales[columna] = formatTwoDecimals(sumaColumna);
        });
        // Sumar la columna `TOTALES` y asignarlo en la fila de totales
        const totalGenerales = datosConTotales.reduce((suma: number, fila: Record<string, string | number>) => {
            return suma + (typeof fila["TOTALES"] === "number" ? fila["TOTALES"] : toNumber(fila["TOTALES"] as string));
        }, 0);
        filaTotales["TOTALES"] = formatTwoDecimals(totalGenerales);
        // Formatear todos los números en la fila de totales para mostrar 2 decimales
        Object.keys(filaTotales).forEach(key => {
            if (key !== "Municipio") {
                filaTotales[key] = (filaTotales[key] as number).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            }
        });
        // Agregar la fila de totales al final
        datosConTotales.push(filaTotales);
        setResponseData(datosConTotales);
    } catch (error) {
        console.error("Error al obtener el reporte:", error);
        setError("Hubo un error al obtener el reporte.");
    } finally {
        setLoading(false);
    }
};


export const guardarDatosMontos = async (
    titulo: string,
    fecha: any, // Puedes cambiar a Dayjs si usas Day.js
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatal",
    responseData: any[],
    setResponseData: (data: any[]) => void, // 👈 Agregado aquí
    setLoading: (loading: boolean) => void,
    setError: (error: string | null) => void,
    navigate: (path: string) => void // Recibe navigate como argumento
) => {

    setError(null);
    const errores = [];

    if (!fecha) {
        errores.push("La fecha es obligatoria.");
    }
    if (!titulo.trim()) {
        errores.push("El título es obligatorio.");
    }
    if (!responseData || responseData.length === 0) {
        errores.push("No hay datos en el reporte para guardar.");
    }
    if (errores.length > 0) {
        Swal.fire({
            icon: "error",
            title: "Error al guardar",
            html: `<ul style="text-align: left;">${errores.map(e => `<li>${e}</li>`).join("")}</ul>`,
            confirmButtonText: "Entendido",
            confirmButtonColor: 'black',
        });
        return;
    }
    const confirmacion = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Se guardarán los datos en el sistema.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, guardar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#15212f",
        cancelButtonColor: "#bda889",
    });
    if (!confirmacion.isConfirmed) {

        return;
    }
    const datosProcesados = responseData
        .filter(fila => !("Municipio" in fila) || fila.Municipio !== "Totales") // Eliminar la fila donde Municipio existe o es "Totales"
        .map(({ Municipio, Totales, OrdenSFTGNL, ...resto }) => resto); // Eliminar las claves "Municipio", "TOTALES" y "OrdenSFTGNL"
    const datosParaGuardar = {
        titulo, // Se asigna directamente en lugar de info.titulo
        fecha: dayjs(fecha).format("YYYY-MM-DD"), // Se formatea la fecha correctamente
        data: datosProcesados, // Datos sin "Municipio", "Totales" ni "OrdenSFTGNL"
    };
    try {
        const response = await axios.post(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/alta`,
            datosParaGuardar,
            {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                    "Content-Type": "application/json", // Si es necesario
                },
            }
        );
        setResponseData(response.data);
        // Mostrar SweetAlert de éxito y recargar la página
        await Swal.fire({
            title: "Guardado exitoso",
            text: "Los datos se han guardado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
        navigate("/admin/participaciones/federales/montos");
    } catch (error: any) {
        console.error("Error al guardar los datos:", error);
        // Obtener el mensaje de error del backend
        const mensajeError = error.response?.data?.message || "Hubo un error al guardar los datos.";
        setError(mensajeError);
        // Mostrar el mensaje exacto del backend en la alerta
        Swal.fire({
            title: "Error",
            text: mensajeError, // Mostrar mensaje recibido del backend
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
    }
};

export const mostrarDatosListaMontos = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatal",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void
) => {
    setLoading(true);
    setError(null);
    try {
        const url = `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/lista`; // Corrección en la concatenación de la URL
        const { data } = await axios.get(url, {
            headers: {
                // Authorization: `Bearer ${token}`, 
                Authorization: localStorage.getItem("jwtToken") || "",
                "Content-Type": "application/json", // Si es necesario
            },
        });
        setResponseData(data.data);
    } catch (error: any) {
        console.error("Error al obtener los datos:", error);
        setError("Hubo un error al obtener los datos.");
    } finally {
        setLoading(false);
    }
};




export const mostrarDatosDetalle = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatal",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void,
    setTitulo: (titulo: string) => void,  // <-- Agregar setTitulo aquí
    setEstatus: Dispatch<SetStateAction<flujoEnum>>,
    idRegistro: string
) => {
    setLoading(true);
    setError(null);
    try {
        const url = `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/detalle/${idRegistro}`; // Corrección en la concatenación de la URL
        const response = await axios.get(url, {
            headers: {
                // Authorization: `Bearer ${token}`, 
                Authorization: localStorage.getItem("jwtToken") || "",
                "Content-Type": "application/json", // Si es necesario
            },
        });
        let datosConTotales = response.data.data || [];
        if (datosConTotales.length > 0) {
            const columnasNumericas = Object.keys(datosConTotales[0]).filter((key) => key !== "municipio");
            const toNumber = (valor: string | number): number => {
                return typeof valor === "number" ? valor : parseFloat(valor) || 0;
            };
            const formatTwoDecimals = (num: number): string => {
                return num.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            };
            // Crear la fila de totales con el nombre "Total"
            const filaTotal: Record<string, string | number> = { municipio: "TOTALES" };
            columnasNumericas.forEach((columna) => {
                const suma = datosConTotales.reduce((suma: number, fila: Record<string, string | number>) => {
                    return suma + toNumber(fila[columna] as string);
                }, 0);
                // Formatear el número con 2 decimales
                filaTotal[columna] = formatTwoDecimals(suma);
            });
            // Agregar la fila de "Total" después del último municipio
            datosConTotales.push(filaTotal);
        }
        setResponseData(datosConTotales);
        setTitulo(response.data.info.titulo);
        setEstatus(response.data.info.estatus);
    } catch (error: any) {
        console.error("Error al obtener los datos:", error);
        setError("Hubo un error al obtener los datos.");
    } finally {
        setLoading(false);
    }
};


export const FlujoMontos = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatales",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void,
    estatus: string,
    idRegistro: string,
    accion: "avanzar" | "devolver"
) => {
    const confirmacion = await Swal.fire({
        title: "¿Deseas actualizar el estatus?",
        text: "Esta acción actualizará el estatus del registro.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, actualizar",
        confirmButtonColor: "#15212f",
        cancelButtonColor: "#bda889",
        cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) {
        return; // El usuario canceló la acción
    }
    const siguienteEstatus = FlujoStatus(estatus, accion);
    const datosParaGuardar = { estatus: siguienteEstatus };
    setLoading(true);
    try {
        const response = await axios.put(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/actualiza/${idRegistro}/`,
            datosParaGuardar,
            {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                    "Content-Type": "application/json", // Si es necesario
                },
            }
        );
        setResponseData(response.data);
        // Mostrar SweetAlert de éxito y recargar la página
        await Swal.fire({
            title: "Actualización exitosa",
            text: "El estatus se ha actualizado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
        window.location.reload(); // Recargar la página después de la confirmación
    } catch (error: any) {
        setError(error.message);
        Swal.fire({
            icon: "error",
            title: "Mensaje",
            text:
                "(" + error.response?.status + ") " + error.response?.data?.message,
        });
    } finally {
        setLoading(false);
    }
};

export const EditarTitulo = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatal",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    cancelarRegistro: Function,
    setError: (error: string | null) => void,
    idRegistro: string,
    titulo: string,
) => {
    const confirmacion = await Swal.fire({
        title: "¿Deseas guardar el nuevo título?",
        text: "Esta acción actualizará el título del registro.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, actualizar",
        confirmButtonColor: "#15212f",
        cancelButtonColor: "#bda889",
        cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) {
        cancelarRegistro();
        return; // El usuario canceló la acción
    }
    const datosParaGuardar = { titulo };
    setLoading(true);
    try {
        const response = await axios.put(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/edita/${idRegistro}/`,
            datosParaGuardar,
            {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                    "Content-Type": "application/json", // Si es necesario
                },
            }
        );
        setResponseData(response.data);
        // Mostrar SweetAlert de éxito y recargar la página
        await Swal.fire({
            title: "Actualización exitosa",
            text: "El estatus se ha actualizado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
        window.location.reload();
    } catch (error: any) {
        const mensajeError = error.response?.data?.message || error.message || "Hubo un error al actualizar los datos.";
        setError(mensajeError);
        Swal.fire({
            title: "Error",
            text: mensajeError,
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
    } finally {
        setLoading(false);
    }
};

export const mostrarDatosListaAcuerdos = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatal",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void
) => {
    setLoading(true);
    setError(null);
    try {
        const url = `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/acuerdos/lista`; // Corrección en la concatenación de la URL
        const { data } = await axios.get(url, {
            headers: {
                Authorization: localStorage.getItem("jwtToken") || "",
                "Content-Type": "application/json", // Si es necesario
            },
        });
        setResponseData(data.data);
    } catch (error: any) {
        console.error("Error al obtener los datos:", error);
        setError("Hubo un error al obtener los datos.");
    } finally {
        setLoading(false);
    }
};


export const guardarDocumentosAcuerdos = async (
    titulo: string,
    fecha: any,
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatal",
    documento: File | null,
    tipo_publicacion_id: string,
    setLoading: (loading: boolean) => void,
    setError: (error: string | null) => void,
    navigate: (path: string) => void
) => {
    setError(null);
    const errores = [];
    if (!titulo.trim()) {
        errores.push("El título es obligatorio.");
    }
    if (!documento) {
        errores.push("Debe adjuntar un documento.");
    }
    if (!tipo_publicacion_id) {
        errores.push("El tipo de publicación es obligatorio.");
    }
    if (errores.length > 0) {
        Swal.fire({
            icon: "error",
            title: "Error al guardar",
            html: `<ul style="text-align: left;">${errores.map(e => `<li>${e}</li>`).join("")}</ul>`,
            confirmButtonText: "Entendido",
            confirmButtonColor: 'black',
        });
        return;
    }
    const confirmacion = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Se guardará el documento en el sistema.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, guardar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#15212f",
        cancelButtonColor: "#bda889",
    });

    if (!confirmacion.isConfirmed) {
        return;
    }
    try {
        setLoading(true);
        const formData = new FormData();
        formData.append('titulo', titulo);
        formData.append('fecha', fecha);
        formData.append('tipo_publicacion_id', tipo_publicacion_id);
        if (documento) {
            formData.append('file', documento);
        }
        const response = await axios.post(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/acuerdos/alta`,
            formData,
            {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                    "Content-Type": "multipart/form-data",
                },
            }
        );
        // Mostrar SweetAlert de éxito
        await Swal.fire({
            title: "Guardado exitoso",
            text: "El documento se ha guardado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
        navigate("/admin/participaciones/federales/acuerdos");
        return response.data;

    } catch (error: any) {
        console.error("Error al guardar el documento:", error);
        let mensajeError = "Hubo un error al guardar el documento.";
        // Caso cuando el error viene como HTML
        if (typeof error.response?.data === 'string') {
            // Extraer solo la primera línea del error del HTML
            const errorMatch = error.response.data.match(/<pre>(.*?)<br>/);
            if (errorMatch && errorMatch[1]) {
                mensajeError = errorMatch[1].trim(); // "MulterError: File too large"
            }
        }
        // Caso estándar para errores JSON
        else if (error.response?.data?.message) {
            mensajeError = error.response.data.message;
        }
        else if (error.response?.data?.error) {
            mensajeError = error.response.data.error;
        }
        setError(mensajeError);
        await Swal.fire({
            title: "Error",
            text: mensajeError,
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
    }
    finally {
        setLoading(false);
    }
}


export const obtenerDocumentoAcuerdos = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatal",
    setLoading: (loading: boolean) => void,
    setError: (error: string | null) => void,
    setTitulo: (titulo: string) => void,
    setTipoPublicacion: (titulo: string) => void,
    setEstatus: Dispatch<SetStateAction<flujoEnum>>,
    idRegistro: string,
    setPdfUrl: (url: string | null) => void
) => {
    setLoading(true);
    setError(null);
    setPdfUrl(null);
    try {
        const urlDetalle = `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/acuerdos/detalle/${idRegistro}`;
        const responseDetalle = await axios.get(urlDetalle, {
            headers: {
                Authorization: localStorage.getItem("jwtToken") || "",
            }
        });
        if (!responseDetalle.data.success) {
            throw new Error("La solicitud no fue exitosa");
        }
        const documentoInfo = responseDetalle.data.data;
        if (documentoInfo.file?.FILE) {
            const byteCharacters = atob(documentoInfo.file.FILE);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const pdfBlob = new Blob([byteArray], { type: documentoInfo.file.TIPO || 'application/pdf' });
            const url = URL.createObjectURL(pdfBlob);
            setPdfUrl(url);
        }
        setTitulo(documentoInfo.titulo);
        setEstatus(documentoInfo.estatus);
        setTipoPublicacion(documentoInfo.tipo_publicacion_id);
    } catch (error: any) {
        console.error('Error en obtenerDocumentoAcuerdos:', {
            message: error.message,
            response: error.response,
            stack: error.stack
        });
        setError(error.response?.data?.message ||
            error.message ||
            "Error al obtener el documento");
    } finally {
        setLoading(false);
        console.groupEnd();
    }
};

export const FlujoAcuerdos = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatal",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void,
    estatus: string,
    idRegistro: string,
    accion: "avanzar" | "devolver",
) => {
    const siguienteEstatus = FlujoStatus(estatus, accion);
    const datosParaGuardar = { estatus: siguienteEstatus };
    // Mostrar SweetAlert de confirmación
    const confirmacion = await Swal.fire({
        title: "¿Deseas actualizar el estatus?",
        text: "Esta acción actualizará el estatus del registro.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, actualizar",
        confirmButtonColor: "#15212f",
        cancelButtonColor: "#bda889",
        cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) {
        return; // El usuario canceló la acción
    }
    setLoading(true);
    try {
        const response = await axios.put(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/acuerdos/actualiza/${idRegistro}/`,
            datosParaGuardar,
            {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                    "Content-Type": "application/json", // Si es necesario
                },
            }
        );
        setResponseData(response.data);
        // Mostrar SweetAlert de éxito y recargar la página
        await Swal.fire({
            title: "Actualización exitosa",
            text: "El estatus se ha actualizado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
        window.location.reload();
    } catch (error) {
        console.error("Error al actualizar los datos:", error);
        setError("Hubo un error al actualizar los datos.");
        // Mostrar SweetAlert de error
        Swal.fire({
            title: "Error",
            text: "No se pudo actualizar el estatus. Inténtalo de nuevo.",
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
    } finally {
        setLoading(false);
    }
};

export const EditarAcuerdo = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatal",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    cancelarRegistro: Function,
    setError: (error: string | null) => void,
    idRegistro: string,
    datos: {
        titulo: string;
        tipo_publicacion?: string;
        doc_nuevo?: boolean;
        file?: File | null;
    },
    estatus: string,
) => {
    const confirmacion = await Swal.fire({
        title: "¿Deseas guardar el nuevo acuerdo?",
        text: "Esta acción actualizará el acuerdo.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, actualizar",
        confirmButtonColor: "#15212f",
        cancelButtonColor: "#bda889",
        cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) {
        cancelarRegistro();
        return; // El usuario canceló la acción
    }

    setLoading(true);
    try {
        const baseHeaders = {
            Authorization: localStorage.getItem("jwtToken") || "",
        };
        let requestData;
        let config: AxiosRequestConfig = { headers: baseHeaders };
        if (estatus === "DEVUELTO" && datos.doc_nuevo && datos.file) {
            requestData = new FormData();
            requestData.append("titulo", datos.titulo);
            requestData.append("tipo_publicacion", datos.tipo_publicacion || "");
            requestData.append("doc_nuevo", "true");
            requestData.append("file", datos.file);
        } else {
            requestData = {
                titulo: datos.titulo,
                ...(estatus === "DEVUELTO" && {
                    tipo_publicacion: datos.tipo_publicacion,
                    doc_nuevo: datos.doc_nuevo || false,
                }),
            };
            config.headers = {
                ...baseHeaders,
                "Content-Type": "application/json",
            };
        }
        const response = await axios.put(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/acuerdos/edita/${idRegistro}/`,
            requestData,
            config
        );
        setResponseData(response.data);
        await Swal.fire({
            title: "Actualización exitosa",
            text: "El registro se ha actualizado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });

        window.location.reload();
    } catch (err: unknown) {
        console.error("Error detallado:", {
            rawError: err,
            isAxiosError: axios.isAxiosError(err),
            isMulterError: isMulterError(err),
            responseData: axios.isAxiosError(err) ? err.response?.data : null
        });
        let mensaje: string;
        // 1. Verificar si es error directo de Multer (no envuelto en Axios)
        if (isMulterError(err)) {
            mensaje = "El archivo es demasiado grande. Por favor, sube un archivo más pequeño.";
        }
        // 2. Verificar si es error de Axios
        else if (axios.isAxiosError(err)) {
            // Verificar si el error viene del backend (500 con detalles de Multer)
            if (err.response?.status === 500 && err.response?.data?.includes('MulterError: File too large')) {
                mensaje = "El archivo excede el tamaño máximo permitido.";
            }
            // Otros errores de Axios
            else {
                mensaje = err.response?.data?.message ||
                    err.response?.data?.error ||
                    err.message ||
                    "Error al procesar la solicitud";
            }
        }
        // 3. Otros tipos de errores
        else if (err instanceof Error) {
            mensaje = err.message;
        }
        else {
            mensaje = "Ocurrió un error inesperado";
        }
        Swal.fire({
            title: "Error",
            text: mensaje,
            icon: "error",
            confirmButtonText: "Entendido"
        });
        return false;
    }
    finally {
        setLoading(false);
    }
    function isMulterError(err: unknown): err is { code: string, message: string } {
        if (typeof err !== 'object' || err === null) return false;
        return 'code' in err && (err.code === 'LIMIT_FILE_SIZE' || err.code === 'LIMIT_UNEXPECTED_FILE');
    }
};

export const FlujoMemorias = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatales",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void,
    estatus: string,
    idRegistro: string,
    accion: "avanzar" | "devolver"
) => {
    const siguienteEstatus = FlujoStatus(estatus, accion);
    const datosParaGuardar = { estatus: siguienteEstatus };
    // Mostrar SweetAlert de confirmación
    if (estatus !== "DEVUELTO") {
        // Si el estatus es diferente de "DEVUELTO", mostrar la alerta de confirmación
        const confirmacion = await Swal.fire({
            title: "¿Estás seguro?",
            text: "Esta acción actualizará el estatus del registro.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sí, actualizar",
            confirmButtonColor: "#15212f",
            cancelButtonColor: "#bda889",
            cancelButtonText: "Cancelar",
        });
        if (!confirmacion.isConfirmed) {
            return;
        }
    }
    setLoading(true);
    try {
        const response = await axios.put(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/memorias/actualiza/${idRegistro}/`,
            datosParaGuardar,
            {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                    "Content-Type": "application/json", // Si es necesario
                },
            }
        );
        setResponseData(response.data);
        // Mostrar SweetAlert de éxito y recargar la página
        await Swal.fire({
            title: "Actualización exitosa",
            text: "El estatus se ha actualizado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
        window.location.reload(); // Recargar la página después de la confirmación
    } catch (error) {
        console.error("Error al actualizar los datos:", error);
        setError("Hubo un error al actualizar los datos.");
        // Mostrar SweetAlert de error
        Swal.fire({
            title: "Error",
            text: "No se pudo actualizar el estatus. Inténtalo de nuevo.",
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
    } finally {
        setLoading(false);
    }
};